import { useUtilisateurService } from '@europrocurement/l2d-domain';
import { DomaineJsonldDomaineRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type Domaine = {
    id: number;
    libelle: string;
};

type DomainesState = DataState & {
    data: Array<Domaine>;
};

const INIT_DOMAINES_CTX = 'init_domaines_ctx';

type Action = { type: 'init_domaines_ctx'; payload: Array<Domaine> };

export const DomainesContext = createContext<DomainesState | null>(null);
export const DomainesDispatchContext = createContext<React.Dispatch<Action> | null>(null);

export const domaineApiToStateTransformer = (item: DomaineJsonldDomaineRead): Domaine => ({
    id: item.ddmId as number,
    libelle: (item.ddmLibelle ?? '') as string,
});

function domainesReducer(_state: DomainesState, action: Action): DomainesState {
    switch (action.type) {
        case INIT_DOMAINES_CTX:
            return { loaded: true, data: [...action.payload] };
        default:
            throw new Error(`Unknown action: ${(action as Action).type}`);
    }
}

export const DomainesProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(domainesReducer, { loaded: false, data: [] });
        const { domainesApi } = useUtilisateurService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            domainesApi
                .apiDomainesGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_DOMAINES_CTX,
                        payload: response.data['hydra:member'].map(domaineApiToStateTransformer),
                    });
                })
                .catch((e) => console.error(e));
        }, [domainesApi, autoload]);

        return (
            <DomainesContext.Provider value={state}>
                <DomainesDispatchContext.Provider value={dispatch}>
                    {children}
                </DomainesDispatchContext.Provider>
            </DomainesContext.Provider>
        );
    };

export function useDomainesCtx() {
    const state = useContext(DomainesContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getDomaines = (): Array<Domaine> => state?.data || [];

    const getDomaineById = (id: number): Domaine | undefined =>
        (state?.data || []).find((d) => d.id === id);

    return { isDataLoaded, getDomaines, getDomaineById };
}

export function useDomainesCtxDispatch() {
    return useContext(DomainesDispatchContext);
}
